import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Upload, X, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";

const VIBES = [
  { id: "nostalgic", label: "NOSTALGIC", desc: "warm · bittersweet" },
  { id: "hype", label: "HYPE", desc: "loud · anthemic" },
  { id: "cinematic", label: "CINEMATIC", desc: "Nolan-core" },
  { id: "emotional", label: "EMOTIONAL", desc: "A24 vulnerable" },
];

export default function CreateEpisode() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [title, setTitle] = useState("");
  const [vibe, setVibe] = useState("cinematic");
  const [contextText, setContextText] = useState("");
  const [files, setFiles] = useState([]); // {id, url, name}
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const handleFiles = async (selected) => {
    if (!selected || selected.length === 0) return;
    setUploading(true);
    try {
      for (const f of Array.from(selected)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFiles((prev) => [
          ...prev,
          { id: data.id, url: fileUrl(data.id), name: data.original_filename },
        ]);
      }
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (id) => setFiles(files.filter((f) => f.id !== id));

  const handleGenerate = async () => {
    if (!title.trim()) return toast.error("Give your episode a title");
    if (files.length === 0) return toast.error("Upload at least one photo");
    setGenerating(true);
    try {
      const { data } = await api.post("/episodes", {
        title: title.trim(),
        vibe,
        context_text: contextText.trim(),
        media_ids: files.map((f) => f.id),
      });
      if (data.status === "failed") {
        toast.error("Generation failed: " + (data.error || "unknown"));
        setGenerating(false);
        return;
      }
      toast.success("Your trailer is ready");
      navigate(`/watch/${data.id}`);
    } catch (e) {
      toast.error("Failed to create episode");
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="create-episode-page">
      <Navbar />

      <div className="pt-28 pb-32 px-6 sm:px-12 max-w-5xl mx-auto cinema-fade-in">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF2A2A] mb-3">
          New Production
        </div>
        <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-none">
          Cast your <span className="text-[#FF2A2A]">moments.</span>
        </h1>

        {/* Title */}
        <div className="mt-16">
          <label className="font-mono text-xs uppercase tracking-[0.3em] text-white/50">
            Episode title
          </label>
          <input
            data-testid="episode-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="My Senior Year"
            className="mt-3 w-full bg-transparent border-0 border-b-2 border-white/10 focus:border-[#FF2A2A] outline-none font-display font-black text-4xl sm:text-5xl tracking-tighter py-3 placeholder:text-white/15 transition-colors"
          />
        </div>

        {/* Vibe */}
        <div className="mt-16">
          <label className="font-mono text-xs uppercase tracking-[0.3em] text-white/50">
            Pick the vibe
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            {VIBES.map((v) => (
              <button
                key={v.id}
                data-testid={`vibe-${v.id}`}
                onClick={() => setVibe(v.id)}
                className={`p-5 text-left border transition-all ${
                  vibe === v.id
                    ? "border-[#FF2A2A] bg-[#FF2A2A]/10 shadow-[inset_0_0_0_1px_#FF2A2A]"
                    : "border-white/10 hover:border-white/30 bg-white/[0.02]"
                }`}
              >
                <div className="font-display font-black text-lg tracking-tight">{v.label}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-2">
                  {v.desc}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Context */}
        <div className="mt-16">
          <label className="font-mono text-xs uppercase tracking-[0.3em] text-white/50">
            Director&apos;s notes <span className="text-white/30">(optional)</span>
          </label>
          <textarea
            data-testid="context-text-input"
            value={contextText}
            onChange={(e) => setContextText(e.target.value)}
            placeholder="A few lines about the story. Names, the season, why it matters."
            rows={3}
            className="mt-3 w-full bg-white/[0.03] border border-white/10 focus:border-white/30 outline-none font-mono text-sm p-4 placeholder:text-white/20 transition-colors"
          />
        </div>

        {/* Upload */}
        <div className="mt-16">
          <label className="font-mono text-xs uppercase tracking-[0.3em] text-white/50">
            Upload your reel
          </label>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            data-testid="file-upload-input"
          />
          <button
            data-testid="file-upload-zone"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="mt-3 w-full border-2 border-dashed border-white/15 hover:border-[#FF2A2A] hover:bg-[#FF2A2A]/5 p-12 sm:p-16 text-center transition-all group disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#FF2A2A]" />
            ) : (
              <Upload className="w-8 h-8 mx-auto text-white/40 group-hover:text-[#FF2A2A] transition-colors" />
            )}
            <div className="font-display font-bold text-2xl tracking-tight mt-4">
              {uploading ? "Loading film stock..." : "Drop your photos & clips"}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-2">
              JPG · PNG · MP4 · MOV — up to 50MB each
            </div>
          </button>

          {files.length > 0 && (
            <div data-testid="uploaded-files-grid" className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-6">
              {files.map((f) => (
                <div key={f.id} className="relative aspect-square group overflow-hidden border border-white/10">
                  <img src={f.url} alt={f.name} className="w-full h-full object-cover" />
                  <button
                    data-testid={`remove-file-${f.id}`}
                    onClick={() => removeFile(f.id)}
                    className="absolute top-1 right-1 bg-black/80 hover:bg-[#FF2A2A] p-1 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Generate */}
        <div className="mt-20 flex items-center gap-4">
          <button
            data-testid="generate-episode-button"
            onClick={handleGenerate}
            disabled={generating || uploading}
            className="group inline-flex items-center gap-3 bg-[#FF2A2A] hover:bg-[#FF5555] text-white font-mono uppercase tracking-widest text-sm px-10 py-5 transition-colors disabled:opacity-60"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Rolling cameras...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 fill-white" />
                Generate trailer
              </>
            )}
          </button>
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
            ~30s render time
          </div>
        </div>
      </div>
    </div>
  );
}
